import React, { useState } from 'react';
import { Grid, Container, FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton, FormHelperText } from '@material-ui/core';
import { useHistory } from "react-router-dom";
import { useDialog } from 'muibox';
import { defaultAlertOptions } from 'config/muibox';
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import EditLayout from 'pages/layout/edit/EditLayout';
import { Loading } from 'components';
import { mensajesGenerales } from 'config/mensajes';
import AuthService from 'services/axios/AuthService';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const mensajes = {
  ...mensajesGenerales,
  formTitle: 'Editar Perfil',
  modificadoConExito: 'Fue modificado con éxito.',
  formId: 'perfilUsuarioForm',
}

const validationSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required(mensajes.formValidacion.campoObligatorio),
  newPassword: yup
    .string()
    .required(mensajes.formValidacion.campoObligatorio),
  newPasswordConfirmation: yup.string()
    .oneOf([yup.ref('newPassword')], 'Las contraseñas deben coincidir')
});

interface ChangePasswordFields {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export default function EditarPerfilUsuarioPage() {
  const history = useHistory();
  const dialog = useDialog();
  const [fetchedData, setFetchedData] = useState<any>();
  const [showPassword, setShowPassword] = useState(false);

  const defaultValues: ChangePasswordFields = {
    currentPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
  };

  const methods = useForm<ChangePasswordFields>({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema)
  });

  const { errors, handleSubmit, control } = methods;

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchedData(await AuthService.fetchCurrentUserFromAPI());
      } catch (error) {
        setFetchedData({ error: error.message })
        error && //if not cancelled 
          dialog.alert({
            ...defaultAlertOptions,
            title: mensajes.formValidacion.haOcurridoUnError,
            message: `${error.message}`
          });
      }
    }
    fetchData();
  }, [dialog]);

  const onSubmit: SubmitHandler<ChangePasswordFields> = async (data: ChangePasswordFields) => {
    try {
      await AuthService.changePassword(data.currentPassword, data.newPassword, data.newPasswordConfirmation);
      await dialog.alert({ ...defaultAlertOptions, message: mensajes.modificadoConExito });
      history.goBack();
    } catch (error) {
      error && //if not cancelled 
        dialog.alert({
          ...defaultAlertOptions,
          title: `${error.message}`,
          message: `${error.errors}`
        });
    }
  }
  //SUBMIT FUNCTIONS: END

  return (
    <EditLayout
      title={mensajes.formTitle}
      onBackIconClick={() => { history.goBack() }}
      onAcceptClick={handleSubmit(onSubmit)}
      showButtons={!fetchedData?.error}
      formId={mensajes.formId}
    >
      {
        !fetchedData && <Loading />
      }
      {
        fetchedData && !fetchedData.error &&
        < Container maxWidth={false}>
          <form onSubmit={handleSubmit(onSubmit)} id={mensajes.formId} noValidate autoComplete="off" >
            <Container maxWidth="sm" style={{ paddingTop: 24, paddingLeft: 24, paddingRight: 24 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <FormControl variant="outlined"
                        fullWidth required
                        error={!!errors.currentPassword}
                      >
                        <InputLabel htmlFor="outlined-adornment-current-password">
                          {"Contraseña Actual"}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-current-password"
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => { setShowPassword(!showPassword) }}
                                edge="end"
                              >
                                {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                          labelWidth={150}
                        />
                        {errors.currentPassword && errors.currentPassword.message &&
                          < FormHelperText > {errors.currentPassword.message}</FormHelperText>
                        }
                      </FormControl>
                    }
                    name="currentPassword"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <FormControl variant="outlined"
                        fullWidth required
                        error={!!errors.newPassword}
                      >
                        <InputLabel htmlFor="outlined-adornment-new-password">
                          {"Nueva Contraseña"}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-new-password"
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => { setShowPassword(!showPassword) }}
                                edge="end"
                              >
                                {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                          labelWidth={150}
                        />
                        {errors.newPassword && errors.newPassword.message &&
                          < FormHelperText > {errors.newPassword.message}</FormHelperText>
                        }
                      </FormControl>
                    }
                    name="newPassword"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <FormControl variant="outlined"
                        fullWidth required
                        error={!!errors.newPasswordConfirmation}
                      >
                        <InputLabel htmlFor="outlined-adornment-new-password-confirmation">
                          {"Repetir Nueva Contraseña"}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-new-password-confirmation"
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => { setShowPassword(!showPassword) }}
                                edge="end"
                              >
                                {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                          labelWidth={200}
                        />
                        {errors.newPasswordConfirmation && errors.newPasswordConfirmation.message &&
                          < FormHelperText > {errors.newPasswordConfirmation.message}</FormHelperText>
                        }
                      </FormControl>
                    }
                    name="newPasswordConfirmation"
                    control={control}
                  />
                </Grid>
              </Grid>
            </Container>
          </form>
        </Container>
      }
    </EditLayout >
  );
}